import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, ShieldCheck, Mail, Fingerprint } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { decodeRequestOptions, encodeCredential, passkeysSupported } from "@/lib/webauthn";

/**
 * Three-stage login UI:
 *   "credentials" → password form
 *   "2fa"         → 2FA code form (TOTP, email-code, or recovery)
 */
export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const dest = from?.pathname?.startsWith("/admin") && from.pathname !== "/admin/login" ? from.pathname + (from.search || "") : "/admin";
  const { refresh } = useAuth();

  const [stage, setStage] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [challenge, setChallenge] = useState(null); // {challenge_token, methods, email}
  const [code, setCode] = useState("");
  const [method, setMethod] = useState("totp");
  const [emailSent, setEmailSent] = useState(false);

  const submitCreds = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      if (r.data.requires_2fa) {
        setChallenge(r.data);
        setMethod(r.data.methods[0] || "totp");
        setStage("2fa");
      } else {
        // Legacy localStorage cleared in case it lingered.
        localStorage.removeItem("receipts_token");
        localStorage.removeItem("receipts_admin_email");
        await refresh();
        toast.success("Welcome back, editor.");
        navigate(dest);
      }
    } catch {
      toast.error("Invalid credentials");
    } finally { setLoading(false); }
  };

  const sendEmailCode = async () => {
    setLoading(true);
    try {
      await api.post("/auth/2fa/email/send", { challenge_token: challenge.challenge_token });
      setEmailSent(true);
      toast.success("A 6-digit code was sent to your email.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to send code");
    } finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/2fa/verify", {
        challenge_token: challenge.challenge_token,
        code,
        method,
      });
      localStorage.removeItem("receipts_token");
      localStorage.removeItem("receipts_admin_email");
      await refresh();
      toast.success("Verified. Welcome back.");
      navigate(dest);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally { setLoading(false); }
  };

  const signInWithPasskey = async () => {
    if (!passkeysSupported()) {
      toast.error("This browser doesn't support passkeys.");
      return;
    }
    setLoading(true);
    try {
      const begin = await api.post("/auth/2fa/passkey/authenticate-begin",
        { challenge_token: challenge.challenge_token });
      const cred = await navigator.credentials.get(decodeRequestOptions(begin.data));
      await api.post("/auth/2fa/passkey/authenticate-finish", {
        challenge_token: challenge.challenge_token,
        credential: encodeCredential(cred),
      });
      localStorage.removeItem("receipts_token");
      localStorage.removeItem("receipts_admin_email");
      await refresh();
      toast.success("Passkey verified. Welcome back.");
      navigate(dest);
    } catch (err) {
      console.error("Passkey sign-in failed", err);
      toast.error(err?.response?.data?.detail || err?.message || "Passkey sign-in failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20">
      <div className="border border-black bg-white p-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-black flex items-center justify-center">
            {stage === "2fa" ? <ShieldCheck className="w-5 h-5 text-white" /> : <Lock className="w-5 h-5 text-white" />}
          </div>
          <div>
            <div className="overline">/restricted</div>
            <div className="font-display text-2xl font-bold">
              {stage === "2fa" ? "Two-factor check" : "Editor sign-in"}
            </div>
          </div>
        </div>

        {stage === "credentials" && (
          <form onSubmit={submitCreds} className="mt-8 space-y-4" data-testid="admin-login-form">
            <label className="block">
              <span className="overline mb-1.5 block">Email</span>
              <Input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-none border-black/30 h-11" />
            </label>
            <label className="block">
              <span className="overline mb-1.5 block">Password</span>
              <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-none border-black/30 h-11" />
            </label>
            <Button type="submit" disabled={loading} data-testid="admin-login-btn" className="rounded-none bg-black text-white w-full h-12">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        )}

        {stage === "2fa" && challenge && (
          <form onSubmit={submitCode} className="mt-8 space-y-4" data-testid="admin-2fa-form">
            <p className="text-sm text-[#333] leading-relaxed">
              Signed in as <b>{challenge.email}</b>.
            </p>

            {/* Passkey button — only shown when the backend says the account has one enrolled. */}
            {challenge.methods?.includes("passkey") && (
              <Button
                type="button"
                onClick={signInWithPasskey}
                disabled={loading}
                data-testid="2fa-passkey-btn"
                className="rounded-none bg-black text-white w-full h-12"
              >
                <Fingerprint className="w-4 h-4 mr-2" /> Sign in with passkey
              </Button>
            )}

            {/* Method tabs — Recovery is always available so users who lose their
                primary method can still get in with their one-time codes. */}
            <div className="flex border border-black/15">
              {challenge.methods.map((m) => (
                <button
                  key={m}
                  type="button"
                  data-testid={`2fa-method-${m}`}
                  onClick={() => { setMethod(m); setEmailSent(false); setCode(""); }}
                  className={`flex-1 px-3 py-2 text-sm font-mono-plex uppercase tracking-wider border-r border-black/15 ${
                    method === m ? "bg-black text-white" : "bg-white text-[#555] hover:bg-[#FAFAFA]"
                  }`}
                >
                  {m === "totp" ? "Authenticator" : "Email code"}
                </button>
              ))}
              <button
                type="button"
                data-testid="2fa-method-recovery"
                onClick={() => { setMethod("recovery"); setEmailSent(false); setCode(""); }}
                className={`flex-1 px-3 py-2 text-sm font-mono-plex uppercase tracking-wider ${
                  method === "recovery" ? "bg-black text-white" : "bg-white text-[#555] hover:bg-[#FAFAFA]"
                }`}
              >
                Recovery
              </button>
            </div>

            {method === "email" && !emailSent && (
              <Button
                type="button"
                onClick={sendEmailCode}
                disabled={loading}
                data-testid="2fa-send-email-btn"
                variant="outline"
                className="rounded-none border-black w-full h-11"
              >
                <Mail className="w-4 h-4 mr-2" /> {loading ? "Sending…" : "Email me a 6-digit code"}
              </Button>
            )}

            {(method !== "email" || emailSent) && (
              <label className="block">
                <span className="overline mb-1.5 block">
                  {method === "recovery" ? "Recovery code" : "6-digit code"}
                </span>
                <Input
                  data-testid="2fa-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={method === "recovery" ? "RECEIPTS-XXXX-XXXX" : "123 456"}
                  required
                  autoComplete="one-time-code"
                  autoFocus
                  className="rounded-none border-black/30 h-12 text-lg font-mono-plex tracking-widest"
                />
              </label>
            )}

            {(method !== "email" || emailSent) && (
              <Button type="submit" disabled={loading || !code} data-testid="2fa-verify-btn" className="rounded-none bg-black text-white w-full h-12">
                {loading ? "Verifying…" : "Verify & sign in"}
              </Button>
            )}

            <button
              type="button"
              onClick={() => { setStage("credentials"); setCode(""); setChallenge(null); setEmailSent(false); }}
              className="text-sm underline underline-offset-4 text-[#555] hover:text-black"
              data-testid="2fa-back-btn"
            >
              ← Back to sign-in
            </button>
          </form>
        )}

        {stage === "credentials" && (
          <p className="mt-6 text-sm text-[#555]">
            <Link to="/" className="underline underline-offset-4">← Back to site</Link>
          </p>
        )}
      </div>
    </div>
  );
}
